import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, InputBase, LinearProgress, List, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { filterCommands, flattenSearchResults, shouldSearch, toCommands, rankItems, toKnowledgeCommands } from './commands';
import { livyConfig, livyFetch } from 'src/lib/livyTransport';
import { freshToken } from 'src/lib/livyApi';
import { LivyTheme } from './LivyTheme';

/** Event the header search field dispatches; the palette owns its open state, not the header. */
export const PALETTE_EVENT = 'livy:palette';

/**
 * Cmd-K / Ctrl-K anywhere in the app, plus the header search field.
 *
 * `enabled` is the same gate the launcher applies. Binding regardless meant preventDefault ran for
 * everyone, so Cmd-K stopped reaching the browser for every user who has no palette to open.
 */
export function useCommandPalette() {
  let enabled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    const onRequest = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(PALETTE_EVENT, onRequest);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(PALETTE_EVENT, onRequest);
    };
  }, [enabled]);
  return {
    open,
    setOpen
  };
}
function LivyCommandPaletteBody(_ref) {
  let open = _ref.open,
    onClose = _ref.onClose;
  const navigate = useNavigate();
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    query = _useState4[0],
    setQuery = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    cursor = _useState6[0],
    setCursor = _useState6[1];
  const role = localStorage.getItem(USER_SETTINGS_KEYS.userRole);
  const commands = useMemo(() => filterCommands(query, role), [query, role]);
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    hits = _useState8[0],
    setHits = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    articles = _useState0[0],
    setArticles = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    searching = _useState10[0],
    setSearching = _useState10[1];
  // A refused search must not read as an empty one, so the failure is state the list renders,
  // not just a console line nobody sees.
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    searchError = _useState12[0],
    setSearchError = _useState12[1];
  const items = useMemo(() => rankItems([...commands, ...toCommands(hits), ...toKnowledgeCommands(articles)], query), [commands, hits, articles, query]);
  useEffect(() => {
    setCursor(0);
  }, [query]);

  // Debounced so a fast typist issues one query, and aborted so a stale response cannot overwrite
  // the results for what is now on screen.
  useEffect(() => {
    const config = livyConfig();
    if (!open || !config || !shouldSearch(query)) {
      setHits([]);
      setArticles([]);
      setSearching(false);
      setSearchError('');
      return undefined;
    }
    const controller = new AbortController();
    // Cleared as the new search starts, not when it returns: otherwise Enter during the debounce
    // opens the previous query's first result, which is a different property or resident.
    setHits([]);
    setArticles([]);
    setSearchError('');
    setSearching(true);
    const timer = setTimeout(() => {
      // freshToken, not the raw store: these requests are signed here and never pass through the
      // app's refresh interceptor, so an idle session's expired token 401s and the palette used to
      // render that as "No matches". Same helper the chat surfaces use, so there is one refresh.
      const search = freshToken().then(token => {
        if (!token) throw new Error('session expired');
        return token;
      });

      // A failed lookup is reported, never swallowed: a search that fails silently is
      // indistinguishable from a search that found nothing, which is how a broken palette looks
      // like an empty one. AbortError is our own cleanup and says nothing to the user.
      const report = label => e => {
        if ((e === null || e === void 0 ? void 0 : e.name) === 'AbortError') return;
        console.error("[Livy] ".concat(label, " search failed"), e);
        setSearchError('Search is unavailable right now.');
      };
      const entities = search.then(token => livyFetch(config, token, {
        method: 'GET',
        path: "/search?q=".concat(encodeURIComponent(query)),
        signal: controller.signal
      })).then(res => {
        // A 401 or 403 is not an empty result. Treating it as one rendered "No matches", which
        // is how a refused search looks exactly like a working one that found nothing.
        if (!res.ok) throw new Error("search failed (".concat(res.status, ")"));
        return res.json();
      }).then(data => setHits(flattenSearchResults(data.results))).catch(report('entity'));

      // Matched server side over title and body, so an answer inside an article is findable and
      // the whole corpus no longer crosses the wire on every keystroke.
      const knowledge = search.then(token => livyFetch(config, token, {
        method: 'GET',
        path: "/kb/search?q=".concat(encodeURIComponent(query)),
        signal: controller.signal
      })).then(res => {
        if (!res.ok) throw new Error("kb search failed (".concat(res.status, ")"));
        return res.json();
      }).then(data => {
        var _data$articles;
        return setArticles((_data$articles = data.articles) !== null && _data$articles !== void 0 ? _data$articles : []);
      }).catch(report('knowledge'));
      void Promise.all([entities, knowledge]).finally(() => setSearching(false));
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query]);
  const run = cmd => {
    if (!(cmd !== null && cmd !== void 0 && cmd.path)) return;
    onClose();
    setQuery('');
    navigate(cmd.path);
  };
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    fullWidth: true,
    maxWidth: "sm",
    PaperProps: {
      sx: {
        position: 'fixed',
        top: 80,
        m: 0
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '12px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(InputBase, {
    autoFocus: true,
    fullWidth: true,
    value: query,
    placeholder: "Search or jump to\u2026",
    onChange: e => setQuery(e.target.value),
    onKeyDown: e => {
      // One unified list, so keyboard focus never jumps between panes.
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor(c => Math.min(c + 1, items.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor(c => Math.max(c - 1, 0));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        run(items[cursor]);
      }
    },
    inputProps: {
      'aria-label': 'Command palette'
    }
  })), /*#__PURE__*/React.createElement(LinearProgress, {
    sx: {
      height: 2,
      opacity: searching ? 1 : 0,
      transition: 'opacity .15s'
    },
    "aria-label": searching ? 'Searching' : undefined
  }), /*#__PURE__*/React.createElement(List, {
    dense: true,
    sx: {
      maxHeight: 360,
      overflowY: 'auto',
      py: 0
    }
  }, items.map((cmd, i) => /*#__PURE__*/React.createElement(ListItemButton, {
    key: cmd.id,
    selected: i === cursor,
    onClick: () => run(cmd)
  }, /*#__PURE__*/React.createElement(ListItemText, {
    primary: cmd.label,
    secondary: cmd.keywords ? "".concat(cmd.section, " \xB7 ").concat(cmd.keywords) : cmd.section
  }))), items.length === 0 && /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, searching ? 'Searching…' : searchError || (shouldSearch(query) ? 'No matches.' : 'Type to search.')))), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    sx: {
      px: '16px',
      py: '8px',
      borderTop: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "\u2191\u2193 navigate"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "\u21B5 select"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "esc close")));
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyCommandPalette(props) {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyCommandPaletteBody, props));
}